import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';

import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {NoAccess} from 'components/pages/NoAccess';
import {ButtonVariant} from 'components/ui-kit/Button';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {ROUTE} from 'core/routes';
import {FactoryDetailsGate, factoryDetailsState$} from 'models/profile/factories/factoryDetails';
import {
    editDataChanged,
    factoryOperationsState$,
    isEditModalOpenChanged,
} from 'models/profile/factories/factoryOperations';
import {UserPermission} from 'types';
import {EditFactoryRaw, Factory} from 'types/profile';
import {usePermission} from 'utils/custom-hooks';
import {formatNumber} from 'utils/number';

import {FactoryEdit} from '../FactoryEdit';

import {
    EditButton,
    EquipmentItem,
    FactoryHeading,
    FactoryInfo,
    FactoryInfoItem,
    FactoryInfoItemTitle,
    FactoryInfoItemValue,
    FactoryName,
    ItemHeadingContainer,
    ItemHeadingTitle,
    ItemLabel,
    ItemLabelName,
    ItemLabelValue,
    ItemNumber,
    LinesContainer,
    LineStatusTag,
    Title,
} from './styled';

const mapFactoryToEdit = (factory: Factory): EditFactoryRaw => ({
    address: factory.address || '',
    addressId: factory.addressId,
    name: factory.name,
    factoryLines: factory.factoryLines.map((line) => ({
        id: line.id,
        name: line.name,
        productName: line.productName,
        code: line.code,
    })),
});

export const FactoryDetails = () => {
    const {t} = useTranslation();
    const {factory, factoryPending} = useStore(factoryDetailsState$);
    const {editData} = useStore(factoryOperationsState$);
    const history = useHistory();
    const {url} = useRouteMatch();
    const {factoryId}: {factoryId: string} = useParams();

    // const itemNum = (val: number): string => (val > 9 ? String(val) : `0${val}`);

    const onEdit = () => {
        editDataChanged(mapFactoryToEdit(factory!));
        isEditModalOpenChanged(true);
    };

    const hasViewFactoryPermission = usePermission([UserPermission.FACTORY_CARD_VIEW]);
    if (!hasViewFactoryPermission) return <NoAccess />;

    return (
        <Page title={t('pages.profile.title')}>
            <FactoryDetailsGate id={factoryId} />
            <BackTo to={ROUTE.FACTORIES}>{t('pages.profile.equipment.back')}</BackTo>
            <ContentContainer status={factoryPending ? 'loading' : 'default'}>
                {factory && (
                    <>
                        <FactoryHeading>
                            <FactoryName>
                                <p>{t('pages.profile.factories.factoryTitle')}</p>
                                <Title>{factory.name}</Title>
                            </FactoryName>
                            <PermissionAccess
                                permissions={[UserPermission.FACTORY_LINE_CONTROL_CREATE]}
                            >
                                {editData && <FactoryEdit />}
                                <EditButton variant={ButtonVariant.Outline} onClick={onEdit}>
                                    {t('pages.profile.factories.edit')}
                                </EditButton>
                            </PermissionAccess>
                        </FactoryHeading>
                        <FactoryInfo>
                            <FactoryInfoItem>
                                <FactoryInfoItemTitle>
                                    {t('pages.profile.factories.id')}
                                </FactoryInfoItemTitle>
                                <FactoryInfoItemValue>{factory.id}</FactoryInfoItemValue>
                            </FactoryInfoItem>
                            <FactoryInfoItem>
                                <FactoryInfoItemTitle>
                                    {t('pages.profile.factories.address')}
                                </FactoryInfoItemTitle>
                                <FactoryInfoItemValue>{factory.address}</FactoryInfoItemValue>
                            </FactoryInfoItem>
                            <FactoryInfoItem>
                                <FactoryInfoItemTitle>
                                    {t('pages.profile.factories.linesCount')}
                                </FactoryInfoItemTitle>
                                <FactoryInfoItemValue>
                                    {factory.factoryLines.length}
                                </FactoryInfoItemValue>
                            </FactoryInfoItem>
                        </FactoryInfo>
                        <LinesContainer>
                            {factory.factoryLines.map((line) => (
                                <EquipmentItem
                                    // style={{pointerEvents: hasViewLinePermission ? 'auto' : 'none'}}
                                    style={{pointerEvents: 'none'}}
                                    key={line.id}
                                    // onClick={() =>
                                    //     hasViewLinePermission && history.push(`${url}/${line.id}`)
                                    // }
                                >
                                    <ItemHeadingContainer>
                                        <ItemHeadingTitle>
                                            <ItemNumber>{line.id}</ItemNumber>
                                            {line.name}
                                            {/*{t('pages.profile.equipment.line')}*/}
                                        </ItemHeadingTitle>
                                        <LineStatusTag isActive={line.active}>
                                            {line.active
                                                ? t('pages.profile.equipment.active')
                                                : t('pages.profile.equipment.notActive')}
                                        </LineStatusTag>
                                    </ItemHeadingContainer>
                                    {/*<ItemLabel>*/}
                                    {/*    <ItemLabelName>*/}
                                    {/*        {t('pages.profile.equipment.lastActivity')}*/}
                                    {/*    </ItemLabelName>*/}
                                    {/*    <ItemLabelValue>*/}
                                    {/*        {formatDate(line.lastActivityTime)}*/}
                                    {/*    </ItemLabelValue>*/}
                                    {/*</ItemLabel>*/}
                                    <ItemLabel>
                                        <ItemLabelName>
                                            {t('pages.profile.equipment.packs')}
                                        </ItemLabelName>
                                        <ItemLabelValue>
                                            {formatNumber(line.packsCount)}
                                        </ItemLabelValue>
                                    </ItemLabel>
                                    {/*<ItemLabel>*/}
                                    {/*    <ItemLabelName>*/}
                                    {/*        {t('pages.profile.equipment.products')}*/}
                                    {/*    </ItemLabelName>*/}
                                    {/*    <ItemLabelValue>*/}
                                    {/*        {formatNumber(line.productsCount)}*/}
                                    {/*    </ItemLabelValue>*/}
                                    {/*</ItemLabel>*/}
                                    {/*<ItemLabel>*/}
                                    {/*    <ItemLabelName>*/}
                                    {/*        {t('pages.profile.equipment.defect')}*/}
                                    {/*    </ItemLabelName>*/}
                                    {/*    <ItemLabelValue>*/}
                                    {/*        {formatNumber(line.defectPercentage)}*/}
                                    {/*    </ItemLabelValue>*/}
                                    {/*</ItemLabel>*/}
                                </EquipmentItem>
                            ))}
                        </LinesContainer>
                    </>
                )}
            </ContentContainer>
        </Page>
    );
};
